import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Phone,
  MarkerPin01,
  Briefcase02,
  FileSearch02,
} from '@untitled-ui/icons-react'
import StackedPageSheet from '@src/components/StackedPageSheet'
import { useWorkforce } from '../../Context'
import EnquirySwitcher from '../EmployeeDetailsSheet/EnquirySwitcher'

import PhoneNumbersTab from './Tabs/PhoneNumbers'
import AddressRecordsTab from './Tabs/AddressRecordsTab'
import EmploymentRecordsTab from './Tabs/EmploymentRecordsTab'
import FootprintsTab from './Tabs/FootprintsTab'

// The tab set, at module scope so `?tab=` can be validated against it before
// state is seeded. There were five until 28 Aug 2026, when the Mahkamah Agung
// source was DROPPED (cancelled, not deferred) and Court Decision went with it
// — tab, panel and the `court_decisions` block behind it. Do not re-add it
// expecting data to arrive; no data is coming.
const TABS = [
  { name: 'Phone Numbers', icon: Phone },
  { name: 'Address Records', icon: MarkerPin01 },
  { name: 'Employment Records', icon: Briefcase02 },
  { name: 'Footprints', icon: FileSearch02 },
]

export default function RiskSignalsDetailsSheet() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  // `?tab=` is VALIDATED against TABS, not trusted. The portal published
  // `?tab=Court Decision` links until that tab was removed, so those links are
  // still in bookmarks and shared URLs. Unvalidated, such a link left activeTab
  // on a name no tab carries: the body fell through to Phone Numbers while the
  // tab strip highlighted nothing, since it renders on `activeTab === tab.name`.
  // Falling back to the first tab makes a stale link land somewhere coherent.
  const requestedTab = searchParams.get('tab')
  const initialTab = TABS.some((t) => t.name === requestedTab) ? requestedTab : TABS[0].name
  // Same `?enquiry=<uuid>` query param used by EmployeeDetailsSheet — pins
  // the Risk Signals payload to that enquiry's snapshot.
  const selectedEnquiryId = searchParams.get('enquiry') || null
  const [activeTab, setActiveTab] = useState(initialTab)
  const { fetchWorkforceDetail, workforceDetailRiskSignals } = useWorkforce()

  useEffect(() => {
    if (id) fetchWorkforceDetail(id, 'risk_signals', selectedEnquiryId)
  }, [id, selectedEnquiryId, fetchWorkforceDetail])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Phone Numbers':
        return <PhoneNumbersTab />
      case 'Address Records':
        return <AddressRecordsTab />
      case 'Employment Records':
        return <EmploymentRecordsTab />
      case 'Footprints':
        return <FootprintsTab />
      default:
        return <PhoneNumbersTab />
    }
  }

  return (
    <StackedPageSheet backUrl={`/workforce/employee/${id}`} closeUrl="/workforce">
      <div className="flex flex-col gap-8 pb-10">
        {/* Page Header */}
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-3xl font-semibold text-gray-900">Risk & background signals</h1>
              <p className="text-md-regular text-gray-600">
                Manage your team members and their account permissions here.
              </p>
            </div>
            {/* Same switcher as the Report tab — risk-signals payload is also
                pinned to the selected enquiry. */}
            <EnquirySwitcher enquiryHistory={workforceDetailRiskSignals?.enquiry_history} />
          </div>

          {/* Category Tabs */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm w-fit">
            {TABS.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`rounded-md px-4 py-2.5 text-sm font-semibold transition-all ${
                  activeTab === tab.name
                    ? 'bg-gray-50 text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Modular Tab Content */}
        {renderTabContent()}
      </div>
    </StackedPageSheet>
  )
}
